import type { ReactNode } from 'react';
import { Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { initials } from '@/lib/conversationFormat';
import WhatsappIcon from './WhatsappIcon';

/**
 * Building blocks shared by the rows of the Verläufe list: the row button
 * shell, the avatar with its channel badge, and the „ · “-joined detail line.
 */

export type RowShellProps = {
  selected: boolean;
  onSelect: () => void;
  avatar: ReactNode;
  title: string;
  aside: ReactNode;
  line: ReactNode;
  accent?: boolean;
  badges?: ReactNode;
};

export function RowShell({ selected, onSelect, avatar, title, aside, line, accent = false, badges }: RowShellProps) {
  return (
    <button
      type="button"
      data-row
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'group relative flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-card',
        selected ? 'bg-brand-soft ring-1 ring-inset ring-brand/35' : 'hover:bg-accent/70',
      )}
    >
      {avatar}
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[13.5px] font-semibold text-foreground">{title}</span>
            {badges}
          </span>
          <span
            className={cn(
              'flex-shrink-0 text-[11.5px] tabular-nums',
              accent ? 'font-semibold text-brand-strong' : 'text-muted-foreground',
            )}
          >
            {aside}
          </span>
        </span>
        <span className="mt-0.5 block truncate text-[12.5px] leading-snug text-muted-foreground">{line}</span>
      </span>
    </button>
  );
}

export function Avatar({
  name,
  phone,
  live = false,
  channel,
}: {
  name: string | null;
  phone: string;
  live?: boolean;
  channel?: 'phone' | 'whatsapp';
}) {
  return (
    <span className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center" aria-hidden="true">
      <span
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-full bg-muted text-[12.5px] font-bold text-foreground/75',
          live && 'bg-brand-soft text-brand-strong',
        )}
      >
        {channel === 'phone' && !name ? <Phone className="h-4 w-4" /> : initials(name, phone)}
      </span>
      {live && (
        <span className="absolute -inset-[3px] rounded-full border-2 border-brand motion-safe:animate-pulse" />
      )}
      {channel && (
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-card',
            channel === 'whatsapp' ? 'bg-success text-success-foreground' : 'bg-foreground/80 text-background',
          )}
        >
          {channel === 'whatsapp' ? <WhatsappIcon className="h-2.5 w-2.5" /> : <Phone className="h-2.5 w-2.5" />}
        </span>
      )}
    </span>
  );
}

export function joinParts(parts: ReactNode[]): ReactNode {
  const present = parts.filter((p) => p !== null && p !== undefined && p !== false && p !== '');
  return present.map((p, i) => (
    <span key={i}>
      {i > 0 && ' · '}
      {p}
    </span>
  ));
}
